import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import UserPage from 'flarum/forum/components/UserPage';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Dropdown from 'flarum/common/components/Dropdown';
import UserControls from 'flarum/forum/utils/UserControls';
import listItems from 'flarum/common/helpers/listItems';

import ProfileHero from './components/ProfileHero';

app.initializers.add('happydigital-profile-redesign', () => {
  // Mark the page so our LESS can scope itself and never leak into other pages.
  extend(UserPage.prototype, 'oninit', function () {
    this.bodyClass = `${this.bodyClass} App--profileRedesign`;

    // Each profile tab is its own Page subclass, and Page scrolls to the top
    // when it is created. With the tabs in a sticky strip that reads as the
    // page jumping back to the cover on every tab switch, losing the reader's
    // place. Core exposes this flag for exactly this case.
    this.scrollTopOnCreate = false;
  });

  // Replace the whole UserPage view: the redesign moves navigation from the
  // sidebar to a horizontal tab bar under the hero, which the core layout
  // cannot express by extending an ItemList alone.
  override(UserPage.prototype, 'view', function (original) {
    if (!this.user) {
      return <div className="UserPage ProfileRedesign">{LoadingIndicator.component({ display: 'block' })}</div>;
    }

    return (
      <div className="UserPage ProfileRedesign">
        {this.headerControls()}

        <ProfileHero user={this.user} editable={this.user.canEdit() || this.user === app.session.user} />

        <nav className="ProfileRedesign-tabsWrap">
          <ul
            className="ProfileRedesign-tabs"
            oncreate={this.scrollActiveTabIntoView.bind(this)}
            onupdate={this.scrollActiveTabIntoView.bind(this)}
          >
            {listItems(this.navItems().toArray())}
          </ul>
        </nav>

        <div className="container">
          <div className="ProfileRedesign-content">{this.content()}</div>
        </div>
      </div>
    );
  });

  /**
   * Keep the selected tab visible in the horizontal strip.
   *
   * Switching tabs re-renders the list, which resets `scrollLeft` to 0 — so
   * activating a tab near the end scrolled the strip back to the start and hid
   * the tab the user had just chosen.
   *
   * Only scrolls the strip itself: `scrollIntoView` would also scroll the page
   * (and on a sticky strip that means jumping to the top), so the offset is
   * applied to the container directly.
   */
  UserPage.prototype.scrollActiveTabIntoView = function (vnode) {
    const strip = vnode.dom;
    const active = strip.querySelector('li.active');

    if (!active) return;

    // Nothing to do when the strip is not actually scrollable.
    if (strip.scrollWidth <= strip.clientWidth) return;

    const stripBox = strip.getBoundingClientRect();
    const activeBox = active.getBoundingClientRect();
    // Leave a tab's edge visible either side so it reads as "more this way".
    const margin = 24;

    let delta = 0;

    if (activeBox.left < stripBox.left + margin) {
      delta = activeBox.left - stripBox.left - margin;
    } else if (activeBox.right > stripBox.right - margin) {
      delta = activeBox.right - stripBox.right + margin;
    }

    if (delta) {
      // Clamped so the margin above cannot push past either end of the strip.
      strip.scrollLeft = Math.max(0, Math.min(strip.scrollLeft + delta, strip.scrollWidth - strip.clientWidth));
    }
  };

  /**
   * The user controls (message, moderation actions), rendered into the header
   * rather than the hero.
   *
   * Core positions any `.App-primaryControl` at the right of the header on
   * phones, so reusing that class puts the menu where the header's own controls
   * live instead of leaving it floating in the page flow.
   */
  UserPage.prototype.headerControls = function () {
    const controls = UserControls.controls(this.user, this);

    // The cover control is surfaced as a button on the cover itself, so it
    // would otherwise appear twice.
    controls.remove('cover');

    const items = controls.toArray();

    if (!items.length) return null;

    return (
      <Dropdown
        className="ProfileRedesign-controls App-primaryControl"
        menuClassName="Dropdown-menu--right"
        buttonClassName="Button Button--icon"
        label={app.translator.trans('core.forum.user_controls.button')}
        accessibleToggleLabel={app.translator.trans('core.forum.user_controls.toggle_dropdown_accessible_label')}
        icon="fas fa-ellipsis-v"
      >
        {items}
      </Dropdown>
    );
  };

});
